/**
 * Language configuration for the Clinvea UI.
 *
 * Adding a language is a three-step job: add an entry here, create
 * `src/i18n/locales/<code>/` with one JSON file per namespace, and run
 * `npm run i18n:check` to confirm no key is missing. The parity test in
 * `src/i18n/__tests__/locale-parity.test.ts` fails the build if a namespace
 * is added for one language but not another.
 */

export const LANGUAGE_STORAGE_KEY = 'clinvea.language';
export const DEFAULT_LANGUAGE = 'en';

export interface SupportedLanguage {
  /** i18next language code — also the folder name under `locales/`. */
  code: string;
  /** Endonym: the language's name in its own script, which is what a speaker looks for. */
  label: string;
  /** English name, shown alongside the endonym so a non-reader can still identify the row. */
  englishLabel: string;
  /**
   * BCP-47 locale for Intl formatting.
   *
   * `-u-nu-latn` pins the numbering system to Latin digits (0-9). Telugu's CLDR default
   * is already `latn`, so today this is a no-op — it is here as a hard guarantee that a
   * future ICU/CLDR change can never start rendering a dose, a vital sign or a date in
   * Telugu numerals (౦-౯). See the rule in `clinicalSafety.ts`.
   */
  locale: string;
  /**
   * Script this language is written in. Drives which font stack the theme applies —
   * Inter carries no Telugu glyphs, so a Telugu string in Inter renders as tofu boxes.
   */
  script: 'latin' | 'telugu';
}

export const SUPPORTED_LANGUAGES: readonly SupportedLanguage[] = [
  {
    code: 'en',
    label: 'English',
    englishLabel: 'English',
    locale: 'en-IN-u-nu-latn',
    script: 'latin',
  },
  {
    code: 'te',
    label: 'తెలుగు',
    englishLabel: 'Telugu',
    locale: 'te-IN-u-nu-latn',
    script: 'telugu',
  },
] as const;

export const SUPPORTED_LANGUAGE_CODES = SUPPORTED_LANGUAGES.map((l) => l.code);

export function getLanguage(code: string | undefined | null): SupportedLanguage {
  return (
    SUPPORTED_LANGUAGES.find((l) => l.code === code) ??
    SUPPORTED_LANGUAGES.find((l) => l.code === DEFAULT_LANGUAGE)!
  );
}

/**
 * Namespaces mirror the domain folders under `src/components/`, so the namespace a
 * component needs is the folder it lives in. They load on demand — a receptionist who
 * never opens the lab screens never downloads the `lab` bundle.
 *
 * `PRELOADED_NAMESPACES` are bundled into the initial chunk instead, because they render
 * before any route resolves; lazy-loading them would flash untranslated keys on first paint.
 */
export const NAMESPACES = [
  // Hand-authored and reviewed. The codemod is forbidden from writing to these.
  'common',
  'nav',
  'auth',
  'errors',
  'clinical',
  // Domain catalogues, extracted from the components by scripts/i18n-codemod.mjs.
  'admin',
  'appointments',
  'billing',
  'checkin',
  'consent',
  'consultation',
  'doctor',
  'inpatient',
  'lab',
  'pages',
  'patients',
  'pharmacy',
  'portal',
  'prescriptions',
  'referrals',
  'reports',
  'staff',
  'ui',
  'vitals',
  'workspace',
] as const;

export type Namespace = (typeof NAMESPACES)[number];

export const PRELOADED_NAMESPACES: Namespace[] = ['common', 'nav', 'auth', 'errors'];

export const DEFAULT_NAMESPACE: Namespace = 'common';
